import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineLockClosed, HiOutlineLockOpen } from "react-icons/hi";
import { BiPlus } from "react-icons/bi";
import { getOrderVipTreatmentOptions } from "Util/getOrderVipTreatmentOptions";
import * as yup from "yup";

import { Color } from "../../../../../../../components/Form/Button";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../components/Form/Input";
import Select from "../../../../../../../components/Form/Select";
import SkeletonComponent from "../../../../../../../components/Generic/Skeleton";
import { ExternalList } from "../../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { AutomaticSequencing } from "../../../../../../../models/automatic.sequencing";
import { Branch } from "../../../../../../../models/branch";
import { Carrier } from "../../../../../../../models/carrier";
import { Currency } from "../../../../../../../models/currency";
import { JobMode } from "../../../../../../../models/job.mode";
import { JobType } from "../../../../../../../models/jobType";
import { LeadSource } from "../../../../../../../models/leadsource";
import { Order, PreApprovalItem } from "../../../../../../../models/order";
import { RoomJob } from "../../../../../../../models/sales";
import { Statuses } from "../../../../../../../models/statuses";
import { User } from "../../../../../../../models/user";
import AccountsService from "../../../../../../../services/account.service";
import AgentService from "../../../../../../../services/agent.service";
import BranchService from "../../../../../../../services/branch.service";
import CarrierService from "../../../../../../../services/carrier.service";
import CurrencyService from "../../../../../../../services/currency.service";
import JobTypeService from "../../../../../../../services/job.type.service";
import LeadSearchFromService from "../../../../../../../services/lead.source.from.service";
import ModeJobService from "../../../../../../../services/mode.job.service";
import StatusesService from "../../../../../../../services/statuses.service";
import { calcTotalItems } from "../../../../../../../util/calcTotalItems";
import { handleGetConvertValue, handleSaveConvertValue } from "../../../../../../../util/converts";
import { decimalMask, limitDecimalPlaces } from "../../../../../../../util/decimalFunctions";
import {
  AccountType,
  DESTINATION,
  InventoryLink,
  JobStatus,
  Mode,
  JobSubStatus as OrderStatus,
  Status,
  UsedByStatuses,
  UserTypes,
} from "../../../../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import { getOrderTransportationTypeOptions } from "../../../../../../../util/getOrderTransportationTypeOptions";
import { getPersonalPropertyProgramOptions } from "../../../../../../../util/getPersonalPropertyOptions";
import { isAllied } from "../../../../../../../util/isAllied";
import { AccountKeys, AgentKeys, CarrierKeys } from "../../../../../../../util/queries";
import RelinkInventoryModal from "../../../../Shared/RelinkInventoryModal";
import UnlinkInventoryModal from "../../../../Shared/UnlinkInventoryModal";
import useStyles, { SectionTitle, UnlinkVolumeButton } from "./styles";
import { AddContainerButton } from "../../../../../../../components/Shipments/Maintenance/Details/components/Containers/styles";
import {
  DeleteButton,
  MinusIcon,
} from "../../../../../../../components/Shipments/Maintenance/Details/components/Containers/components/Details/styles";
import { GO_SHARE_COMPANY_IDS, GOSSELIN_MOVING_COMPANY_IDS, GOVLOG_COMPANY_NAME } from "Constants";

const ISO_YMD = /^\d{4}-\d{2}-\d{2}$/;

function shiftCalendarDayYmd(ymd: string, deltaDays: number): string | undefined {
  const t = ymd.trim();
  if (!ISO_YMD.test(t)) return undefined;
  const [y, m, d] = t.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + deltaDays);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

interface FormContentProps {
  order?: Order;
  update: (attribute: string, value: any, noChanges?: boolean) => void;
  errors: yup.ValidationError[];
  sequence?: AutomaticSequencing;
  externalList?: ExternalList;
  isFetching?: boolean;
  ordersUsers?: User[];
}

const FormContent = ({
  order,
  update,
  errors,
  sequence,
  externalList,
  isFetching = false,
  ordersUsers,
}: FormContentProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser, agentPermissions, agentCompany } = useAppState();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const [branches, setBranches] = useState<Branch[]>(() => {
    if (!externalList?.branchList || !currentUser?.branches) {
      return externalList?.branchList || [];
    }

    const userBranchesIds = new Set(currentUser.branches.map((branch) => branch.id));

    return externalList.branchList.filter((branch) => userBranchesIds.has(branch.id));
  });
  const [jobType, setJobType] = useState<JobType[]>(externalList?.jobTypeList || []);
  const [statuses, setStatuses] = useState<Statuses[]>(externalList?.statusList || []);
  const [leadSourceFromList, setLeadSourceFromList] = useState<LeadSource[]>(
    externalList?.leadSourceFromList || []
  );
  const [jobModeList, setJobModeList] = useState<JobMode[]>(externalList?.jobModeList || []);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>(externalList?.userList || []);
  const [referenceField, setReferenceField] = useState<string>("");

  const [inventoryToUnlink, setInventoryToUnlink] = useState<InventoryLink | undefined>();
  const [inventoryToRelink, setInventoryToRelink] = useState<InventoryLink | undefined>();

  const [customVolumeQuotedText, setCustomVolumeQuotedText] = useState<string | undefined>();
  const [customVolumeActualText, setCustomVolumeActualText] = useState<string | undefined>();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const vipTreatments = useMemo(() => getOrderVipTreatmentOptions(translate), [translate]);
  const transportationTypes = useMemo(
    () => getOrderTransportationTypeOptions(translate),
    [translate]
  );
  const personalPropertiesProgram = useMemo(() => getPersonalPropertyProgramOptions(), [translate]);

  const useAgentRenderingPermissions = () => {
    const associatedCompaniesThroughAgents: number[] = order?.tasks?.reduce((acc, task) => {
      if (task?.agent?.associatedCompanyId) {
        acc.push(task.agent?.associatedCompanyId);
      }
      return acc;
    }, [] as number[]) || [];

    if (
      agentPermissions && // agentPermissions have been fetched correctly
      currentUser?.company.profileId === 6 || // company is of type MOVING_AGENT
      (// user is not from the company that created the job/order and is from the company associated to the agent that received the order/job
        currentUser?.companyId !== order?.companyId &&
        currentUser?.companyId !== order?.companyId && currentUser?.companyId && associatedCompaniesThroughAgents.includes(currentUser?.companyId)
      )
    ) {
      return { 
        // DETAILS FIELDS
        showReferenceField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.reference === true,
        show2ndReferenceField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.["2ndReference"] === true,
        showOrderTypeField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.orderType === true,
        showTagField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.tag === true,
        showModeField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.mode === true,
        showTransportationTypeField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.transportationType === true,
        showVipTreatmentField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.vipTreatment === true,
        showAccountTypeField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.accountType === true,
        showLeadSourceField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.leadSource === true,
        showScacField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.details?.scac === true,
        // ROLES FIELDS
        showBookingAgentField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.bookingAgent === true,
        showBranchOriginField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.branchOrigin === true,
        showBranchDestinationField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.branchDestination === true,
        showConsultantField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.consultant === true,
        showMoveCoordinatorField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.moveCoordinator === true,
        showManagerField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.roles?.manager === true,
        // WEIGHT AND MEASUREMENTS FIELDS
        showAllowanceField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.weightMeasurements?.allowance === true,
        showInventorySummaryField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.weightMeasurements?.inventorySummary === true,
        // PICKUP FIELDS
        showPickupContactField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.pickup?.contact === true,
        showPickupAddressField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.pickup?.address === true,
        // DELIVERY FIELDS
        showDeliveryContactField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.delivery?.contact === true,
        showDeliveryAddressField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.delivery?.address === true,
        // GENERAL COMMENTS FIELDS
        showGeneralCommentsField: agentPermissions?.[Number(order?.id)]?.order?.sections?.detail?.fields?.generalComments?.view === true,
      }
    }

    return {
      showReferenceField: true,
      show2ndReferenceField: true,
      showOrderTypeField: true,
      showTagField: true,
      showModeField: true,
      showTransportationTypeField: true,
      showVipTreatmentField: true,
      showAccountTypeField: true,
      showLeadSourceField: true,
      showScacField: true,
      showBookingAgentField: true,
      showBranchOriginField: true,
      showBranchDestinationField: true,
      showConsultantField: true,
      showMoveCoordinatorField: true,
      showManagerField: true,
      showAllowanceField: true,
      showInventorySummaryField: true,
      showPickupContactField: true,
      showPickupAddressField: true,
      showDeliveryContactField: true,
      showDeliveryAddressField: true,
      showGeneralCommentsField: true,
    };
  };

  const quotedInventoryIsLocked: boolean = useMemo(() => {
    if (
      (order?.customQuantityQuoted === null || order?.customQuantityQuoted === undefined) &&
      (order?.customVolumeQuoted === null || order?.customVolumeQuoted === undefined)
    ) {
      return true;
    }

    return false;
  }, [order?.customQuantityQuoted, order?.customVolumeQuoted]);

  const actualInventoryIsLocked: boolean = useMemo(() => {
    if (
      (order?.customQuantityActual === null || order?.customQuantityActual === undefined) &&
      (order?.customVolumeActual === null || order?.customVolumeActual === undefined)
    ) {
      return true;
    }

    return false;
  }, [order?.customQuantityActual, order?.customVolumeActual]);

  useEffect(() => {
    if (customVolumeQuotedText === undefined)
      setCustomVolumeQuotedText(
        handleGetConvertValue(
          order?.customVolumeQuoted || 0,
          measurement?.code,
          measurement?.decimalPlaces
        ).toString()
      );
  }, [order?.customVolumeQuoted]);

  useEffect(() => {
    if (customVolumeActualText === undefined)
      setCustomVolumeActualText(
        handleGetConvertValue(
          order?.customVolumeActual || 0,
          measurement?.code,
          measurement?.decimalPlaces
        ).toString()
      );
  }, [order?.customVolumeActual]);

  useEffect(() => {
    if (ordersUsers) {
      setAllUsers(ordersUsers);
    }
  }, [ordersUsers]);

  useEffect(() => {
    if (!externalList?.branchList) return;

    if (!currentUser?.branches?.length) {
      setBranches(externalList.branchList);
      return;
    }

    const userBranchesIds = new Set(currentUser.branches.map((branch) => branch.id));
    setBranches(externalList.branchList.filter((branch) => userBranchesIds.has(branch.id)));
  }, [externalList?.branchList, currentUser?.branches]);

  useEffect(() => {
    if (externalList?.statusList) setStatuses(externalList.statusList);
  }, [externalList?.statusList]);

  useEffect(() => {
    if (externalList?.jobTypeList) setJobType(externalList.jobTypeList);
  }, [externalList?.jobTypeList]);

  useEffect(() => {
    if (externalList?.leadSourceFromList) setLeadSourceFromList(externalList.leadSourceFromList);
  }, [externalList?.leadSourceFromList]);

  useEffect(() => {
    if (externalList?.jobModeList) setJobModeList(externalList.jobModeList);
  }, [externalList?.jobModeList]);

  useQuery(
    ["branches.list", currentUser?.branches],
    () =>
      BranchService.getAll({
        // TODO: uncomment when decide what to do
        // id: currentUser?.branches?.map((branch) => branch.id),
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "ASC"]]),
      }),
    {
      enabled: !externalList?.branchList?.length && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) {
          setBranches(response.data);
        }
      },
    }
  );

  useQuery(
    ["statuses.list"],
    () =>
      StatusesService.getAll({
        usedBy: UsedByStatuses.ORDERS,
        companyId: currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled:
        (!externalList?.statusList?.length || currentUser?.company.profileId === 6) &&
        !!(currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setStatuses(response.data);
        }
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled:
        (!externalList?.jobTypeList?.length || currentUser?.company.profileId === 6) &&
        !!(currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobType(response.data);
        }
      },
    }
  );

  useQuery(
    ["leadSourceFrom.list"],
    () =>
      LeadSearchFromService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !externalList?.leadSourceFromList?.length && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0)
          setLeadSourceFromList(response.data);
      },
    }
  );

  useQuery(
    ["jobMode.list"],
    () =>
      ModeJobService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !externalList?.jobModeList?.length && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobModeList(response.data);
      },
    }
  );

  const { data: accountList } = useQuery({
    queryKey: AccountKeys.getAll([]),
    queryFn: async () => {
      const response = await AccountsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response?.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { data: agentList } = useQuery({
    queryKey: [AgentKeys.all],
    queryFn: async () => {
      const response = await AgentService.getAll({
        createdCompanyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response?.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { data: carrierList } = useQuery({
    queryKey: CarrierKeys.getAll([]),
    queryFn: async () => {
      const response = await CarrierService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response?.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  useQuery(
    ["currencies.list", currentUser?.companyId],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data && Array.isArray(response?.data)) {
          setCurrencies(response.data);
        }
      },
    }
  );

  const consultantName = useMemo(() => {
    if (order?.user) {
      const name = order?.user?.name;
      const lastName = order?.user?.lastName;
      const email = order?.user?.email;

      if ((name || lastName) && email) return `${name ?? ""} ${lastName ?? ""} - ${email ?? ""}`;

      return "";
    }

    return "";
  }, [order?.user]);

  const managerName = useMemo(() => {
    if (order?.manager) {
      const name = order?.manager?.name ?? "";
      const lastName = order?.manager?.lastName ?? "";
      const email = order?.manager?.email ?? "";

      const fullName = `${name} ${lastName}`;
      const hasNameAndEmail = fullName !== "" && email !== "";

      return `${fullName}${hasNameAndEmail ? " - " : ""}${email}`;
    }

    return "";
  }, [order?.manager]);

  const moveCoordinatorName = useMemo(() => {
    if (order?.moveCoordinator) {
      const name = order?.moveCoordinator?.name;
      const lastName = order?.moveCoordinator?.lastName;
      const email = order?.moveCoordinator?.email;

      const fullName = `${name} ${lastName}`;
      const hasNameAndEmail = fullName !== "" && email !== "";

      return `${fullName}${hasNameAndEmail ? " - " : ""}${email}`;
    }

    return "";
  }, [order?.moveCoordinator]);

  const activeUsers = allUsers.filter((user) => user.status === Status.ACTIVE);
  const isMilitary = Boolean(order?.company?.isMilitary ?? currentUser?.company?.isMilitary);

  const sitOutDateNotAfterSitIn = useMemo(() => {
    if (!isMilitary) return false;
    const sitIn = (order?.sitInDate ?? "").trim();
    const sitOut = (order?.sitOutDate ?? "").trim();
    if (!sitIn || !sitOut) return false;
    return !(sitOut > sitIn);
  }, [isMilitary, order?.sitInDate, order?.sitOutDate]);

  const sitInDateMax = useMemo(
    () =>
      isMilitary && (order?.sitOutDate ?? "").trim()
        ? shiftCalendarDayYmd((order?.sitOutDate ?? "").trim(), -1)
        : undefined,
    [isMilitary, order?.sitOutDate]
  );

  const sitOutDateMin = useMemo(
    () =>
      isMilitary && (order?.sitInDate ?? "").trim()
        ? shiftCalendarDayYmd((order?.sitInDate ?? "").trim(), 1)
        : undefined,
    [isMilitary, order?.sitInDate]
  );

  const showSpecificFieldsByCompany = !!GO_SHARE_COMPANY_IDS.includes(currentUser?.companyId!);
  const hasToShowDescription =
    !!GOSSELIN_MOVING_COMPANY_IDS.includes(currentUser?.companyId!) || showSpecificFieldsByCompany;

  const handleSetInventoryFields = (type: "inventory", field: "quantity" | "volume") => {
    let value = 0;

    if (order) {
      const roomJob = order[type];

      if (roomJob) {
        roomJob?.forEach((roomJob: RoomJob) => {
          if (field === "quantity") {
            if (roomJob.itemsJob && roomJob.itemsJob.length > 0) {
              roomJob.itemsJob.forEach((item) => {
                if (
                  item?.itemJob?.destination?.name?.toLowerCase() !==
                  DESTINATION.NOT_GOING?.toLowerCase()
                ) {
                  value += Number(item.itemJob.quantity);
                }
              });
            }
            if (roomJob.materials && roomJob.materials.length > 0) {
              roomJob.materials.forEach((material) => {
                if (
                  material?.serviceBooking?.isItem &&
                  material?.destination?.name?.toLowerCase() !==
                    DESTINATION.NOT_GOING?.toLowerCase()
                ) {
                  value += Number(material.quantity);
                }
              });
            }
          } else {
            if (roomJob && roomJob.itemsJob && roomJob.itemsJob.length > 0) {
              roomJob.itemsJob.forEach((item) => {
                if (
                  item?.itemJob?.destination?.name?.toLowerCase() !==
                  DESTINATION.NOT_GOING?.toLowerCase()
                ) {
                  value += Number(item.itemJob.volume) * Number(item.itemJob.quantity);
                }
              });
            }
            if (roomJob.materials && roomJob.materials.length > 0) {
              roomJob.materials.forEach((material) => {
                if (
                  material?.serviceBooking?.isItem &&
                  material?.destination?.name?.toLowerCase() !==
                    DESTINATION.NOT_GOING?.toLowerCase()
                ) {
                  value += Number(material.quantity) * Number(material.volume);
                }
              });
            }
          }
        });
      }
    }

    return field === "quantity"
      ? value
      : handleGetConvertValue(Number(value), measurement?.code || "m3", measurement?.decimalPlaces);
  };

  const quotedQuantity = useMemo(() => {
    return quotedInventoryIsLocked
      ? order?.quotedQuantity ?? (handleSetInventoryFields("inventory", "quantity") || 0).toString()
      : (order?.customQuantityQuoted || 0)?.toString();
  }, [quotedInventoryIsLocked, order?.quotedQuantity, order?.customQuantityQuoted]);

  const quotedVolume = useMemo(() => {
    return quotedInventoryIsLocked
      ? order?.quotedVolume ?? (handleSetInventoryFields("inventory", "volume") || 0).toString()
      : customVolumeQuotedText;
  }, [quotedInventoryIsLocked, order?.quotedVolume, customVolumeQuotedText]);

  const { totalVolume, totalQuantity } = useMemo(
    () =>
      calcTotalItems({
        items: order?.actualInventory,
        measurement,
        weightMeasurement: measurementWeight,
      }),
    [order?.actualInventory, measurementWeight, measurement]
  );

  const renderVolumeLeaderValue = () => {
    if (order?.mode === Mode.Air || !order?.sizeConfirmation)
      return handleGetConvertValue(0, measurement?.code, measurement?.decimalPlaces).toString();

    return handleGetConvertValue(
      Number(order?.sizeConfirmation),
      measurement?.code,
      measurement?.decimalPlaces
    ).toString();
  };

  const disableReferenceEdit = () => {
    if (order) {
      if (order.sequence && order.sequence > 0) {
        return true;
      }
      if (order?.jobStatus !== OrderStatus.BOOKED && currentUser?.typeUser !== UserTypes.ADMIN) {
        return true;
      }
    }

    return false;
  };

  const updateOrder = (attribute: string, value: any, noChanges?: boolean) => {
    update(attribute, value, noChanges);
  };

  const buildDefaultPreApprovalItem = useCallback((): PreApprovalItem => {
    const defaultCurrencyCode =
      currentUser?.company?.currency?.code ??
      currencies?.find((currency) => !!currency?.code)?.code ??
      "";
    return {
      goShareId: "",
      code: "",
      description: "",
      count: null,
      unit: "",
      currency: defaultCurrencyCode,
      rate: null,
      total: null,
    };
  }, [currentUser?.company?.currency?.code, currencies]);

  const handleAddPreApprovalItem = () => {
    const currentItems = order?.preApprovals ?? [];
    update("preApprovals", [...currentItems, buildDefaultPreApprovalItem()]);
  };

  const handleUpdatePreApprovalItem = (
    index: number,
    field: keyof PreApprovalItem,
    value: string | number | null
  ) => {
    const currentItems = [...(order?.preApprovals ?? [])];
    currentItems[index] = {
      ...currentItems[index],
      [field]: value,
    };
    update("preApprovals", currentItems);
  };

  const handleRemovePreApprovalItem = (index: number) => {
    const currentItems = [...(order?.preApprovals ?? [])];
    currentItems.splice(index, 1);
    update("preApprovals", currentItems);
  };

  useEffect(() => {
    if (!order?.id && allUsers?.length) {
      let consultant: User | undefined = {} as User;
      let manager: User | undefined = {} as User;

      if (allUsers?.length) {
        const current = allUsers?.find((user) => user.id === currentUser?.id);

        if (current) {
          consultant = current;
          manager = current?.manager || current?.usersCompanies?.[0]?.manager;
        }
      }

      update(
        "all",
        {
          ...order,
          user: { ...consultant },
          userId: consultant?.id,
          manager: { ...manager },
          managerId: manager?.id,
        },
        true
      );
    }
  }, [allUsers]);

  useEffect(() => {
    if (!order?.id && !order?.accountType?.trim().length) {
      updateOrder("accountType", AccountType.PRIVATE);
    }
  }, [order]);

  useEffect(() => {
    if (!order?.id && !order?.jobStatus) {
      updateOrder("jobStatus", JobStatus.BOOKED);
    }
  }, [order]);

  useEffect(() => {
    if (!order?.id && !order?.branchFromId && currentUser?.branches?.length) {
      const branch = currentUser?.branches?.find(
        (branch) => branch.companyId === currentUser?.companyId
      );

      updateOrder("branchFromId", branch?.id);
    }
  }, [order, currentUser]);

  useEffect(() => {
    if (order) {
      if (order?.reference) setReferenceField(order.reference);
      else if (sequence) {
        updateOrder(
          "reference",
          `${sequence?.prefix ? sequence.prefix : ""}${
            sequence?.nextValue ? sequence.nextValue : ""
          }`
        );
      }
    }
  }, [order, sequence]);

  const {
    showReferenceField,
    show2ndReferenceField,
    showOrderTypeField,
    showTagField,
    showModeField,
    showTransportationTypeField,
    showVipTreatmentField,
    showAccountTypeField,
    showLeadSourceField,
    showScacField,
    showBookingAgentField,
    showBranchOriginField,
    showBranchDestinationField,
    showConsultantField,
    showMoveCoordinatorField,
    showManagerField,
    showAllowanceField,
    showInventorySummaryField,
    showPickupContactField,
    showPickupAddressField,
    showDeliveryContactField,
    showDeliveryAddressField,
    showGeneralCommentsField,
  } = useAgentRenderingPermissions();

  return (
    <Grid container direction="row" alignItems="flex-start" spacing={2}>
      {(showReferenceField || show2ndReferenceField || showOrderTypeField || showTagField || showModeField || showTransportationTypeField || showVipTreatmentField || showAccountTypeField || showLeadSourceField || showScacField) && (
        <Grid item xs={12} className={classes.title}>
          <SectionTitle>{translate("details")}</SectionTitle>
        </Grid>
      )}
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        spacing={2}
        className={classes.fieldsContainer}
      >
        {showReferenceField && (
          <Grid item xs={12} md={3}>
            <Input
              required
              name="reference"
              label={translate("reference")}
              value={referenceField}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setReferenceField(event.target.value)
              }
              onBlur={() => update("reference", referenceField)}
              error={fieldHasError("reference")}
              errorMessage={getErrorByField("reference")}
              disabled={disableReferenceEdit()}
              rightIcon={order?.sequence && <>{`${translate("sequence")}: ${order?.sequence}`}</>}
            />
          </Grid>
        )}

        {show2ndReferenceField && (
          <Grid item xs={12} md={3}>
            <Input
              name="secondReference"
              label={translate("secondReference")}
              placeholder={translate("secondReference")}
              value={order?.secondReference ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                update("secondReference", event.target.value)
              }
              error={fieldHasError("secondReferenceField")}
              errorMessage={getErrorByField("secondReferenceField")}
              disabled={disableReferenceEdit()}
            />
          </Grid>
        )}

        {showOrderTypeField && (
          <Grid item xs={12} md={3}>
            <Select
              required
              name="jobType"
              defaultValue=""
              label={translate("orderType")}
              value={order?.jobTypeId ?? ""}
              onChange={(event: SelectChangeEvent<any>) => {
                update("jobTypeId", event.target.value);
              }}
              error={fieldHasError("jobTypeId") || fieldHasError("type")}
              errorMessage={getErrorByField("jobTypeId") || getErrorByField("type")}
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {jobType.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {hasToShowDescription ? type.description : type.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Select
            name="status"
            label={translate("status")}
            disabled={true}
            defaultValue=""
            value={order?.jobStatus}
            onChange={(event: SelectChangeEvent<any>) => update("jobStatus", event.target.value)}
            error={fieldHasError("status")}
            errorMessage={getErrorByField("status")}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {statuses?.map((status) => (
              <MenuItem key={status.id} value={status.name}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        spacing={2}
        className={classes.fieldsContainer}
      >
        {showTagField && (
          <Grid item container sm md={6} lg={3}>
            <AutoSuggest
              label={translate("tag")}
              placeholder="Press enter to add"
              value={order && order.tag && order.tag.length > 0 ? order?.tag?.split(",") : []}
              multiple
              freeSolo
              getOptionLabel={(option: string) => option}
              onChange={(tags: string[]) => {
                update(
                  "tag",
                  tags
                    .map((tag) => {
                      return tag.trim();
                    })
                    .join(",")
                );
              }}
            />
          </Grid>
        )}

        {showModeField && (
          <Grid item xs={12} md={3}>
            <Select
              name="mode"
              label={translate("mode")}
              value={order?.mode}
              onChange={(event: SelectChangeEvent<any>) => update("mode", event.target.value)}
              defaultValue=""
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {jobModeList.map((mode: any) => (
                <MenuItem key={mode.id} value={mode.value}>
                  {translate(mode.value.toLowerCase()) || mode.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showTransportationTypeField && (
          <Grid item xs={12} md={3}>
            <Select
              name="transportationType"
              defaultValue=""
              label={translate("transportationType")}
              value={order?.transportationType || ""}
              onChange={(event) => update("transportationType", event.target.value)}
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {transportationTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showVipTreatmentField && (
          <Grid item xs={12} md={3}>
            <Select
              required
              name="vipTreatment"
              defaultValue=""
              label={translate("vipTreatment")}
              value={order?.vipTreatment || ""}
              onChange={(event: SelectChangeEvent<any>) =>
                update("vipTreatment", event.target.value)
              }
              error={fieldHasError("vipTreatment")}
              errorMessage={getErrorByField("vipTreatment")}
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {vipTreatments.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showAccountTypeField && (
          <Grid item xs={12} md={3}>
            <Select
              name="accountType"
              defaultValue={AccountType.PRIVATE}
              label={translate("accountType")}
              value={order?.accountType}
              onChange={(event: SelectChangeEvent<any>) =>
                update("accountType", event.target.value)
              }
              placeholder={translate("selectAnOption")}
            >
              <MenuItem value={AccountType.PRIVATE}>{translate("private")}</MenuItem>
              <MenuItem value={AccountType.CORPORATE}>{translate("corporate")}</MenuItem>
            </Select>
          </Grid>
        )}

        {order?.accountType?.toLowerCase() === AccountType.CORPORATE.toLowerCase() && (
          <>
            <Grid item xs={12} md={3}>
              <Select
                name="accountName"
                label={translate("account")}
                value={order?.accountId}
                onChange={(event: SelectChangeEvent<any>) =>
                  update("accountId", event.target.value)
                }
                placeholder={translate("selectAnOption")}
              >
                {accountList?.map((item: any) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Input
                disabled
                name="accountContact"
                label={translate("accountContact")}
                value={order?.account?.email}
              />
            </Grid>
          </>
        )}
        {showLeadSourceField && (
          <Grid item xs={12} md={3}>
            <Select
              name="leadSource"
              label={translate("leadSourceFrom")}
              defaultValue="none"
              value={order?.leadSourceFrom}
              onChange={(event: SelectChangeEvent<any>) =>
                update("leadSourceFrom", event.target.value)
              }
              placeholder={translate("selectAnOption")}
            >
              {leadSourceFromList.map((item: any) => (
                <MenuItem key={item.id} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showSpecificFieldsByCompany && (
          <Grid item xs={12} md={3}>
            <Select
              name="personalPropertyProgram"
              label={translate("personalPropertyProgram")}
              value={order?.personalPropertyProgram}
              onChange={(event: SelectChangeEvent<any>) =>
                update("personalPropertyProgram", event.target.value)
              }
              placeholder={translate("selectAnOption")}
            >
              {personalPropertiesProgram?.map((item: any) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showScacField && !isMilitary && (
          <Grid item xs={12} md={3}>
            <AutoSuggest
              label={translate("scac").toUpperCase()}
              placeholder={translate("searchForAnOption")}
              value={order?.carrier}
              inputValue={order?.carrier ? `${order?.carrier?.scac}-${order?.carrier?.name}` : ""}
              getOptionLabel={(carrier?: Carrier) =>
                carrier ? `${carrier?.scac}-${carrier?.name}` : ""
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {`${option.scac}-${option.name}`}
                </li>
              )}
              getOptionSelected={(option?: Carrier, value?: Carrier) => option?.id === value?.id}
              options={carrierList}
              onChange={(carrier: Carrier) =>
                update("all", {
                  ...order,
                  carrierId: carrier?.id || null,
                  carrier: carrier || null,
                })
              }
            />
          </Grid>
        )}

        {isMilitary && (
          <Grid item xs={12} md={3}>
            <Input
              name="grade"
              label={translate("grade")}
              placeholder={translate("gradePlaceholder")}
              value={order?.grade ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                update("grade", event.target.value)
              }
            />
          </Grid>
        )}
      </Grid>

      {isMilitary && (
        <>
          <Grid item xs={12} className={classes.title}>
            <SectionTitle>{translate("scacInformation")}</SectionTitle>
          </Grid>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={2}
            className={classes.fieldsContainer}
          >
            <Grid item xs={12} md={4}>
              <AutoSuggest
                label={translate("scacCode")}
                placeholder={translate("searchForAnOption")}
                value={order?.carrier}
                inputValue={order?.carrier ? `${order?.carrier?.scac}-${order?.carrier?.name}` : ""}
                getOptionLabel={(carrier?: Carrier) =>
                  carrier ? `${carrier?.scac}-${carrier?.name}` : ""
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {`${option.scac}-${option.name}`}
                  </li>
                )}
                getOptionSelected={(option?: Carrier, value?: Carrier) => option?.id === value?.id}
                options={carrierList}
                onChange={(carrier: Carrier) =>
                  update("all", {
                    ...order,
                    carrierId: carrier?.id || null,
                    carrier: carrier || null,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="scacDescription"
                label={translate("scacDescription")}
                value={order?.scacDescription ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("scacDescription", event.target.value)
                }
                placeholder={translate("scacDescription")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="scacPhone"
                label={translate("phone")}
                value={order?.scacPhone ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("scacPhone", event.target.value)
                }
                placeholder={translate("phone")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="scacEmail"
                label={translate("email")}
                value={order?.scacEmail ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("scacEmail", event.target.value)
                }
                placeholder={translate("email")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="scacClaimsPhone"
                label={translate("claimsPhone")}
                value={order?.scacClaimsPhone ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("scacClaimsPhone", event.target.value)
                }
                placeholder={translate("claimsPhone")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="scacClaimsEmail"
                label={translate("claimsEmail")}
                value={order?.scacClaimsEmail ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("scacClaimsEmail", event.target.value)
                }
                placeholder={translate("claimsEmail")}
              />
            </Grid>
          </Grid>
        </>
      )}

      {(showBookingAgentField || showBranchOriginField || showBranchDestinationField || showConsultantField || showMoveCoordinatorField || showManagerField) && (
        <Grid item xs={12} className={classes.title}>
          <SectionTitle>{translate("roles")}</SectionTitle>
        </Grid>
      )}

      <Grid
        container
        direction="row"
        alignItems="flex-start"
        spacing={2}
        className={classes.fieldsContainer}
      >
        {showBookingAgentField && (
          <Grid item xs={12} md={3}>
            <Select
              name="bookingAgentId"
              label={translate("bookingAgent")}
              displayEmpty
              placeholder={translate("selectAnOption")}
              value={order?.bookingAgentId}
              onChange={(event) => {
                update("bookingAgentId", event.target.value);
              }}
            >
              {agentList?.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.entity}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showBranchOriginField && (
          <Grid item xs={12} md={3}>
            <Select
              name="branchOriginId"
              label={translate("branchOrigin")}
              displayEmpty
              placeholder={translate("selectAnOption")}
              value={order?.branchFromId}
              onChange={(event) => {
                update("branchFromId", event.target.value);
              }}
            >
              {branches?.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showBranchDestinationField && (
          <Grid item xs={12} md={3}>
            <Select
              name="branchDestinationId"
              label={translate("branchDestination")}
              defaultValue=""
              displayEmpty
              placeholder={translate("selectAnOption")}
              value={order?.toBranchId}
              onChange={(event) => {
                update("toBranchId", event.target.value);
              }}
            >
              {branches &&
                branches.map(
                  (branch) =>
                    branch.name &&
                    branch.name.trim() !== "" && (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    )
                )}
            </Select>
          </Grid>
        )}
      </Grid>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        spacing={2}
        className={classes.fieldsContainer}
      >
        {showConsultantField && (
          <Grid item xs={12} md={3}>
            <SkeletonComponent isLoading={isFetching}>
              <AutoSuggest
                label={translate("consultant")}
                placeholder={translate("searchForAConsultant")}
                inputValue={consultantName}
                getOptionLabel={(user?: User) =>
                  user && user?.name?.trim()?.length && user?.email?.trim()?.length
                    ? `${user.name} ${user.lastName ?? ""} - ${user.email}`
                    : ""
                }
                getOptionSelected={(option?: User, value?: User) => option?.id === value?.id}
                options={activeUsers}
                onChange={(user: User) => {
                  if (user) {
                    if (user.manager) {
                      update("all", {
                        ...order,
                        user: { ...user },
                        userId: user.id,
                        manager: { ...user.manager },
                        managerId: user.manager.id,
                      });
                    } else {
                      update("all", {
                        ...order,
                        user: { ...user },
                        userId: user.id,
                        manager: { ...user },
                        managerId: user.id,
                      });
                    }
                  } else {
                    update("all", {
                      ...order,
                      user: null,
                      userId: null,
                      manager: null,
                      managerId: null,
                    });
                  }
                }}
              />
            </SkeletonComponent>
          </Grid>
        )}

        {showMoveCoordinatorField && (
          <Grid item xs={12} md={3}>
            <SkeletonComponent isLoading={isFetching}>
              <AutoSuggest
                label={translate("moveCoordinator")}
                inputValue={moveCoordinatorName}
                placeholder="Search for move coordinator"
                getOptionLabel={(moveCoordinator?: User) =>
                  moveCoordinator && moveCoordinator?.name && moveCoordinator?.email
                    ? `${moveCoordinator?.name} ${moveCoordinator?.lastName ?? ""} - ${
                        moveCoordinator?.email
                      }`
                    : ""
                }
                getOptionSelected={(option: User, value: User) => option.id === value.id}
                options={isAllied(currentUser?.company) ? allUsers : activeUsers}
                onChange={(moveCoordinator: User) => {
                  update("all", {
                    ...order,
                    moveCoordinator: moveCoordinator ? { ...moveCoordinator } : null,
                    moveCoordinatorId: moveCoordinator ? moveCoordinator?.id : null,
                  });
                }}
              />
            </SkeletonComponent>
          </Grid>
        )}

        {showManagerField && (
          <Grid item xs={12} md={3}>
            <SkeletonComponent isLoading={isFetching}>
              <AutoSuggest
                label={translate("manager")}
                placeholder="Search manager"
                inputValue={managerName}
                getOptionLabel={(manager?: User) =>
                  manager && manager?.name && manager?.email
                    ? `${manager.name} ${manager?.lastName ?? ""} - ${manager.email}`
                    : ""
                }
                getOptionSelected={(option: User, value: User) => option.id === value.id}
                options={activeUsers}
                onChange={(manager: User) => {
                  update("all", {
                    ...order,
                    manager: manager ? { ...manager } : null,
                    managerId: manager ? manager.id : null,
                  });
                }}
                error={fieldHasError("manager")}
                errorMessage={getErrorByField("manager")}
              />
            </SkeletonComponent>
          </Grid>
        )}
      </Grid>

      {isMilitary && (
        <>
          <Grid item xs={12} className={classes.title}>
            <SectionTitle>{translate("storageInTransitOriginSit")}</SectionTitle>
          </Grid>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={2}
            className={classes.fieldsContainer}
          >
            <Grid item xs={12} md={4}>
              <Input
                name="sitInDate"
                type="date"
                label={translate("sitInDate")}
                value={order?.sitInDate ?? ""}
                max={sitInDateMax}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("sitInDate", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="sitOutDate"
                type="date"
                label={translate("sitOutDate")}
                value={order?.sitOutDate ?? ""}
                min={sitOutDateMin}
                error={sitOutDateNotAfterSitIn}
                errorMessage={
                  sitOutDateNotAfterSitIn ? translate("sitOutDateMustBeAfterSitInDate") : undefined
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("sitOutDate", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                name="sitNumber"
                label={translate("sitNumber")}
                value={order?.sitNumber ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update("sitNumber", event.target.value)
                }
                placeholder={translate("sitNumber")}
              />
            </Grid>
          </Grid>
        </>
      )}

      {(showAllowanceField || showInventorySummaryField) && (
        <Grid item xs={12} className={classes.title}>
          <SectionTitle>{translate("weightAndMeasurements")}</SectionTitle>
        </Grid>
      )}

      {showAllowanceField && (
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          spacing={2}
          className={classes.fieldsContainer}
        >
          <Grid item xs={12} md={3}>
            <Input
              name="allowance"
              label={translate("allowance")}
              placeholder={translate("allowance")}
              value={order?.allowance ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                update("allowance", e.currentTarget.value);
              }}
            />
          </Grid>
          {isMilitary && (
            <>
              <Grid item xs={12} md={3}>
                <Input
                  name="grossWeight"
                  label={translate("grossWeight")}
                  placeholder={translate("grossWeight")}
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={order?.grossWeight ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    update("grossWeight", e.currentTarget.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Input
                  name="tareWeight"
                  label={translate("tareWeight")}
                  placeholder={translate("tareWeight")}
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={order?.tareWeight ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    update("tareWeight", e.currentTarget.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Input
                  name="netWeight"
                  label={translate("netWeight")}
                  placeholder={translate("netWeight")}
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={order?.netWeight ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    update("netWeight", e.currentTarget.value);
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}

      {showInventorySummaryField && (
        <Grid container item spacing={2} direction="column">
          <Grid item>
            <span className={classes.subTitle}>{translate("inventoriesSummary")}</span>
          </Grid>
          <Grid
            item
            container
            alignItems="flex-end"
            justifyContent="flex-start"
            direction="row"
            spacing={2}
          >
            <Grid item container xs={3} direction="column" spacing={2}>
              <Grid item>
                <span className={classes.inventoryDesc}>{translate("inventoryLeader")}</span>
              </Grid>
              <Grid
                item
                container
                alignItems="flex-end"
                justifyContent="space-between"
                direction="row"
              >
                <Grid item xs={6}>
                  <Input
                    labelClassName={classes.label}
                    label={translate("items")}
                    name="itemLeader"
                    placeholder={translate("noItems")}
                    disabled
                    value={(totalQuantity || 0).toString()}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Input
                    label={translate("volume")}
                    name="volumeLeader"
                    disabled
                    placeholder={Number(0)?.toFixed(measurement?.decimalPlaces)}
                    value={renderVolumeLeaderValue()}
                    rightIcon={measurement?.code || ""}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item container xs={3} direction="column" spacing={2}>
              <Grid item>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span className={classes.inventoryDesc}>{translate("inventoryQuoted")}</span>
                  <Tooltip
                    placement="right"
                    title={
                      quotedInventoryIsLocked
                        ? translate("inventoryLinked")
                        : translate("inventoryUnlinked")
                    }
                  >
                    <UnlinkVolumeButton
                      onClick={() => {
                        if (quotedInventoryIsLocked) {
                          setInventoryToUnlink(InventoryLink.QUOTED);
                        } else {
                          setInventoryToRelink(InventoryLink.QUOTED);
                        }
                      }}
                    >
                      {quotedInventoryIsLocked ? (
                        <HiOutlineLockClosed size={16} color="#475467" />
                      ) : (
                        <HiOutlineLockOpen size={16} color="#475467" />
                      )}
                    </UnlinkVolumeButton>
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid
                item
                container
                alignItems="flex-end"
                justifyContent="space-between"
                direction="row"
              >
                <Grid item xs={6}>
                  <Input
                    labelClassName={classes.label}
                    label={translate("items")}
                    name="itemQuoted"
                    type="number"
                    placeholder={translate("noItems")}
                    disabled={quotedInventoryIsLocked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { isValid, value } = limitDecimalPlaces(event.target.value || "0", 0);

                      if (isValid) update("customQuantityQuoted", value);
                    }}
                    value={quotedQuantity}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Input
                    label={translate("volume")}
                    name="customVolumeQuoted"
                    placeholder={Number(0)?.toFixed(measurement?.decimalPlaces)}
                    disabled={quotedInventoryIsLocked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const maskedValue = decimalMask(
                        event.target.value,
                        measurement?.decimalPlaces
                      );

                      setCustomVolumeQuotedText(maskedValue);

                      const { isValid, value } = limitDecimalPlaces(
                        maskedValue || "0",
                        measurement?.decimalPlaces
                      );

                      const convertedValue = handleSaveConvertValue(
                        value.toString(),
                        measurement?.code
                      );

                      if (isValid) {
                        update("customVolumeQuoted", convertedValue);
                      }
                    }}
                    rightIcon={measurement?.code || ""}
                    value={quotedVolume}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item container xs={3} direction="column" spacing={2}>
              <Grid item>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span className={classes.inventoryDesc}>{translate("inventoryActual")}</span>
                  <Tooltip
                    placement="right"
                    title={
                      actualInventoryIsLocked
                        ? translate("inventoryLinked")
                        : translate("inventoryUnlinked")
                    }
                  >
                    <UnlinkVolumeButton
                      onClick={() => {
                        if (actualInventoryIsLocked) {
                          setInventoryToUnlink(InventoryLink.ACTUAL);
                        } else {
                          setInventoryToRelink(InventoryLink.ACTUAL);
                        }
                      }}
                    >
                      {actualInventoryIsLocked ? (
                        <HiOutlineLockClosed size={16} color="#475467" />
                      ) : (
                        <HiOutlineLockOpen size={16} color="#475467" />
                      )}
                    </UnlinkVolumeButton>
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid
                item
                container
                alignItems="flex-end"
                justifyContent="space-between"
                direction="row"
              >
                <Grid item xs={6}>
                  <Input
                    labelClassName={classes.label}
                    label={translate("items")}
                    name="itemActual"
                    type="number"
                    placeholder={translate("noItems")}
                    disabled={actualInventoryIsLocked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { isValid, value } = limitDecimalPlaces(event.target.value || "0", 0);

                      if (isValid) update("customQuantityActual", value);
                    }}
                    value={
                      actualInventoryIsLocked
                        ? (totalQuantity || 0).toString()
                        : (order?.customQuantityActual || 0)?.toString()
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <Input
                    label={translate("volume")}
                    name="volumeLeader"
                    placeholder={Number(0)?.toFixed(measurement?.decimalPlaces)}
                    disabled={actualInventoryIsLocked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const maskedValue = decimalMask(
                        event.target.value,
                        measurement?.decimalPlaces
                      );

                      setCustomVolumeActualText(maskedValue);

                      const { isValid, value } = limitDecimalPlaces(
                        maskedValue || "0",
                        measurement?.decimalPlaces
                      );

                      const convertedValue = handleSaveConvertValue(value, measurement?.code);

                      if (isValid) {
                        update("customVolumeActual", convertedValue);
                      }
                    }}
                    value={
                      actualInventoryIsLocked
                        ? (totalVolume || 0)?.toString()
                        : customVolumeActualText
                    }
                    rightIcon={measurement?.code || ""}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {isMilitary && (
        <>
          <Grid item xs={12} className={classes.title}>
            <SectionTitle>{translate("preApprovals")}</SectionTitle>
          </Grid>
          <Grid
            item
            xs={12}>
            <div className={classes.preApprovalTableContainer}>
              <div className={classes.preApprovalTable}>
                <div className={`${classes.preApprovalGridRow} ${classes.preApprovalHeadRow}`}>
                  <div className={classes.preApprovalHeadCell}>{translate("code")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("description")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("count")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("unit")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("currency")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("rate")}</div>
                  <div className={classes.preApprovalHeadCell}>{translate("total")}</div>
                  <div
                    className={`${classes.preApprovalHeadCell} ${classes.preApprovalHeadCellAction}`}
                  />
                </div>
                {(order?.preApprovals ?? []).map((item: PreApprovalItem, index: number) => (
                  <div
                    key={item.id ?? `pre-approval-${index}`}
                    className={`${classes.preApprovalGridRow} ${
                      (order?.preApprovals?.length ?? 0) === 1
                        ? classes.preApprovalBodyRowPlain
                        : index % 2 === 0
                          ? classes.preApprovalBodyRowZebra
                          : classes.preApprovalBodyRowPlain
                    }`}
                  >
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsCode-${index}`}
                          value={item.code ?? ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleUpdatePreApprovalItem(index, "code", event.target.value)
                          }
                          placeholder={translate("code")}
                        />
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsDescription-${index}`}
                          value={item.description ?? ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleUpdatePreApprovalItem(index, "description", event.target.value)
                          }
                          placeholder={translate("description")}
                        />
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsCount-${index}`}
                          type="number"
                          inputProps={{ step: "1" }}
                          value={
                            item.count !== undefined && item.count !== null
                              ? String(item.count)
                              : ""
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.target.value;
                            handleUpdatePreApprovalItem(index, "count", value === "" ? null : value);
                          }}
                          placeholder={translate("count")}
                        />
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsUnit-${index}`}
                          value={item.unit ?? ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleUpdatePreApprovalItem(index, "unit", event.target.value)
                          }
                          placeholder={translate("unit")}
                        />
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Select
                          name={`preApprovalsCurrency-${index}`}
                          value={item.currency ?? ""}
                          onChange={(event: SelectChangeEvent<any>) =>
                            handleUpdatePreApprovalItem(index, "currency", event.target.value)
                          }
                          displayEmpty
                          placeholder={translate("selectAnOption")}
                        >
                          {currencies?.map((currency) => (
                            <MenuItem key={currency.id} value={currency.code}>
                              {currency.code} - {currency.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsRate-${index}`}
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={
                            item.rate !== undefined && item.rate !== null ? String(item.rate) : ""
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.target.value;
                            handleUpdatePreApprovalItem(index, "rate", value === "" ? null : value);
                          }}
                          placeholder={translate("rate")}
                        />
                      </div>
                    </div>
                    <div className={classes.preApprovalBodyCell}>
                      <div className={classes.preApprovalCellControl}>
                        <Input
                          name={`preApprovalsTotal-${index}`}
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={
                            item.total !== undefined && item.total !== null ? String(item.total) : ""
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.target.value;
                            handleUpdatePreApprovalItem(index, "total", value === "" ? null : value);
                          }}
                          placeholder={translate("total")}
                        />
                      </div>
                    </div>
                    <div
                      className={`${classes.preApprovalBodyCell} ${classes.preApprovalBodyCellAction}`}
                    >
                      {(order?.preApprovals?.length ?? 0) >= 1 && (
                        <DeleteButton display="flex" alignItems="center" justifyContent="center">
                          <Tooltip title={translate("removePreApprovalItem")}>
                            <span>
                              <MinusIcon
                                size={24}
                                style={{
                                  marginTop: 0,
                                  marginLeft: 0,
                                }}
                                onClick={() => handleRemovePreApprovalItem(index)}
                              />
                            </span>
                          </Tooltip>
                        </DeleteButton>
                      )}
                    </div>
                  </div>
                ))}
                <div className={classes.preApprovalFooterRow}>
                  <AddContainerButton
                    onClick={handleAddPreApprovalItem}
                    customColor={Color.DEFAULT}
                    startIcon={<BiPlus />}
                  >
                    {translate("addPreApprovalItem")}
                  </AddContainerButton>
                </div>
              </div>
            </div>
          </Grid>
        </>
      )}

      <UnlinkInventoryModal
        open={!!inventoryToUnlink}
        onClose={() => setInventoryToUnlink(undefined)}
        onSave={() => {
          switch (inventoryToUnlink) {
            case InventoryLink.QUOTED: {
              update("all", {
                ...order,
                customQuantityQuoted: 0,
                customVolumeQuoted: handleGetConvertValue(
                  0.0,
                  measurement?.code,
                  measurement?.decimalPlaces
                ),
              });
              setCustomVolumeQuotedText(Number(0)?.toFixed(measurement?.decimalPlaces));
              break;
            }
            case InventoryLink.ACTUAL: {
              update("all", {
                ...order,
                customQuantityActual: 0,
                customVolumeActual: handleGetConvertValue(
                  0.0,
                  measurement?.code,
                  measurement?.decimalPlaces
                ),
              });
              setCustomVolumeActualText(Number(0)?.toFixed(measurement?.decimalPlaces));
              break;
            }
            default:
              break;
          }

          setInventoryToUnlink(undefined);
        }}
      />

      <RelinkInventoryModal
        open={!!inventoryToRelink}
        onClose={() => setInventoryToRelink(undefined)}
        onSave={() => {
          switch (inventoryToRelink) {
            case InventoryLink.QUOTED:
              update("all", {
                ...order,
                customQuantityQuoted: null,
                customVolumeQuoted: null,
              });
              break;
            case InventoryLink.ACTUAL:
              update("all", {
                ...order,
                customQuantityActual: null,
                customVolumeActual: null,
              });
              break;
            default:
              break;
          }

          setInventoryToRelink(undefined);
        }}
      />

      {/* //TODO: Select service in orders create/edit details */}
      {false && (
        <Grid item xs={3}>
          <Select name="service" label={translate("service")} defaultValue="choose" displayEmpty>
            <MenuItem value="choose">Choose</MenuItem>
          </Select>
        </Grid>
      )}
    </Grid>
  );
};

export default FormContent;
